import { BudgetPages } from '../../pages/BudgetPages';
import LiteralsService from 'src/services/literals/LiteralsService';
import { ScreenBreakpoints } from 'src/hooks/useBreakpoints';
import oneXP from 'src/types/one-xp';
import { DashboardModalData, DashboardModalImage } from 'src/types/dashboardModal.model';
import { PopupData, PopupPlacement } from 'src/types/popup.model';
import { ContentVersion, WalkmeModel, WalkmeName, WalkmeVisualMode } from 'src/types/walkme.model';
import adobeTags from 'src/types/adobe-tags';

function GetDashboardModalStep(isNotMobile: boolean): { data: DashboardModalData; dataContentVersion: ContentVersion } {
    return {
        data: {
            title: LiteralsService.getLiteral('TransactionClassificationDashboardModal.title'),
            img: DashboardModalImage.ANNOUNCEMENT,
            nextButtonText: LiteralsService.getLiteral('TransactionClassificationDashboardModal.takeTheTour'),
            skipButtonText: LiteralsService.getLiteral('TransactionClassificationDashboardModal.skipTheTour'),
            listItems: [1, 2, 3].map((n) => ({
                title: LiteralsService.getLiteral(`TransactionClassificationDashboardModal.listItems.item${n}.title`),
                content: LiteralsService.getJsxLiteral(
                    `TransactionClassificationDashboardModal.listItems.item${n}.${isNotMobile ? 'body' : 'mobileBody'}`
                ),
                isMinWidthSmall: isNotMobile
            }))
        },
        dataContentVersion: isNotMobile ? ContentVersion.PRIMARY : ContentVersion.SECONDARY
    };
}

function GetWalkmePopupLiteralTextProps(itemNumber: number, isTypeColumnVisible: boolean): PopupData {
    return {
        content: LiteralsService.getLiteral(
            `TransactionClassificationWalkmePopups.item${itemNumber}.${
                isTypeColumnVisible ? 'content' : 'mobileContent'
            }`
        ),
        nextButtonText: LiteralsService.getLiteral(
            `TransactionClassificationWalkmePopups.item${itemNumber}.nextButtonText`
        ),
        progressText: LiteralsService.getLiteral(
            `TransactionClassificationWalkmePopups.item${itemNumber}.progressText`
        ),
        placement: PopupPlacement.TOP,
        closeButtonAriaLabel: LiteralsService.getLiteral(
            `TransactionClassificationWalkmePopups.item${itemNumber}.closeButtonAriaLabel`
        ),
        nextButtonAriaLabel: LiteralsService.getLiteral(
            `TransactionClassificationWalkmePopups.item${itemNumber}.nextButtonAriaLabel`
        )
    };
}

function GetPopup1(isLargeEnough: boolean): { data: PopupData; dataContentVersion: ContentVersion } {
    return {
        data: {
            ...GetWalkmePopupLiteralTextProps(1, isLargeEnough),
            placement: isLargeEnough ? PopupPlacement.RIGHT : PopupPlacement.BOTTOM
        },
        dataContentVersion: isLargeEnough ? ContentVersion.PRIMARY : ContentVersion.SECONDARY
    };
}

function GetPopup2(isLargeEnough: boolean): { data: PopupData; dataContentVersion: ContentVersion } {
    return {
        data: {
            ...GetWalkmePopupLiteralTextProps(2, isLargeEnough),
            placement: isLargeEnough ? PopupPlacement.RIGHT : PopupPlacement.TOP
        },
        dataContentVersion: isLargeEnough ? ContentVersion.PRIMARY : ContentVersion.SECONDARY
    };
}

function GetPopup3(isTypeColumnVisible: boolean): { data: PopupData; dataContentVersion: ContentVersion } {
    return {
        data: {
            // The "type" column is visible at SM, MD and XL, but not at XS, and LG
            ...GetWalkmePopupLiteralTextProps(3, isTypeColumnVisible),
            placement: PopupPlacement.TOP
        },
        dataContentVersion: isTypeColumnVisible ? ContentVersion.PRIMARY : ContentVersion.SECONDARY
    };
}

function GetPopup4(isCategoryListVisible: boolean): { data: PopupData; dataContentVersion: ContentVersion } {
    return {
        data: {
            // The "category list" is visible at MD, LG, XL, but not at XS and SM.
            ...GetWalkmePopupLiteralTextProps(4, isCategoryListVisible),
            placement: isCategoryListVisible ? PopupPlacement.LEFT : PopupPlacement.TOP
        },
        dataContentVersion: isCategoryListVisible ? ContentVersion.PRIMARY : ContentVersion.SECONDARY
    };
}

function GetPopup5(isLargeEnough: boolean): { data: PopupData; dataContentVersion: ContentVersion } {
    return {
        data: {
            ...GetWalkmePopupLiteralTextProps(5, isLargeEnough),
            placement: PopupPlacement.TOP
        },
        dataContentVersion: isLargeEnough ? ContentVersion.PRIMARY : ContentVersion.SECONDARY
    };
}

function getData(breakpoints: ScreenBreakpoints): WalkmeModel {
    const { isMinWidthSmall, isMinWidthMedium, isMinWidthLarge, isMinWidthExtraLarge } = breakpoints;
    return {
        name: WalkmeName.TRANSACTION_CLASSIFICATION,
        startupStepUserAttributeName: 'transactionClassificationStartupStep',
        featureFlagName: oneXP.FeatureFlags.PlatformCommunications,
        featureFlagEnablementDependsOnDaysSinceReleaseDate: true,
        featureFlagReleaseDatePropertyName: oneXP.FeatureFlagPropertyNames.PlatformCommunications.ReleaseDate,
        featureFlagDaysToShowAfterReleaseDatePropertyName:
            oneXP.FeatureFlagPropertyNames.PlatformCommunications.DaysToShowAfterRelease,
        redirectOnFinish: true,
        redirectOnFinishUrl: BudgetPages.HOME,
        steps: [
            {
                ...GetDashboardModalStep(isMinWidthSmall),
                mode: WalkmeVisualMode.MODAL,
                path: BudgetPages.HOME,
                adobeTags: {
                    widgetName: adobeTags.Walkme.IntroModal.WidgetName,
                    impression: adobeTags.Walkme.IntroModal.ImpressionAction,
                    close: adobeTags.Walkme.IntroModal.CloseTourAction,
                    next: adobeTags.Walkme.IntroModal.NextTourAction,
                    skip: adobeTags.Walkme.IntroModal.SkipTourAction
                }
            },
            {
                ...GetPopup1(isMinWidthSmall),
                mode: WalkmeVisualMode.POPUP,
                path: BudgetPages.HOME,
                adobeTags: {
                    widgetName: adobeTags.Walkme.Popup1.WidgetName,
                    impression: adobeTags.Walkme.Popup1.ImpressionAction,
                    close: adobeTags.Walkme.Popup1.CloseTourAction,
                    next: adobeTags.Walkme.Popup1.NextTourAction
                }
            },
            {
                ...GetPopup2(isMinWidthMedium),
                mode: WalkmeVisualMode.POPUP,
                path: BudgetPages.HOME,
                adobeTags: {
                    widgetName: adobeTags.Walkme.Popup2.WidgetName,
                    impression: adobeTags.Walkme.Popup2.ImpressionAction,
                    close: adobeTags.Walkme.Popup2.CloseTourAction,
                    next: adobeTags.Walkme.Popup2.NextTourAction
                }
            },
            {
                ...GetPopup3(
                    ((): boolean => {
                        // The "type" column is visible at SM, MD and XL, but not at XS, and LG
                        switch (true) {
                            case isMinWidthExtraLarge:
                                return true;
                            case isMinWidthLarge:
                                return false;
                            case isMinWidthMedium:
                                return true;
                            case isMinWidthSmall:
                                return true;
                            default:
                                return false;
                        }
                    })()
                ),
                mode: WalkmeVisualMode.POPUP,
                path: BudgetPages.TRANSACTIONS,
                adobeTags: {
                    widgetName: adobeTags.Walkme.Popup3.WidgetName,
                    impression: adobeTags.Walkme.Popup3.ImpressionAction,
                    close: adobeTags.Walkme.Popup3.CloseTourAction,
                    next: adobeTags.Walkme.Popup3.NextTourAction
                }
            },
            {
                ...GetPopup4(isMinWidthMedium),
                mode: WalkmeVisualMode.POPUP,
                path: BudgetPages.TRANSACTIONS,
                adobeTags: {
                    widgetName: adobeTags.Walkme.Popup4.WidgetName,
                    impression: adobeTags.Walkme.Popup4.ImpressionAction,
                    close: adobeTags.Walkme.Popup4.CloseTourAction,
                    next: adobeTags.Walkme.Popup4.NextTourAction
                }
            },
            {
                ...GetPopup5(isMinWidthSmall),
                mode: WalkmeVisualMode.POPUP,
                path: BudgetPages.TRANSACTIONS_RULES,
                adobeTags: {
                    widgetName: adobeTags.Walkme.Popup5.WidgetName,
                    impression: adobeTags.Walkme.Popup5.ImpressionAction,
                    close: adobeTags.Walkme.Popup5.CloseTourAction,
                    next: adobeTags.Walkme.Popup5.NextTourAction
                }
            }
        ]
    };
}

export default getData;
